import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import { Modal, Row, Col, Button } from 'react-bootstrap'
import styles from 'designer/styles/modal.module.css'
import { Resolver } from '@stoplight/json-ref-resolver'
import PNotify from 'pnotify/dist/es/PNotify'

import httpClient from 'utility/httpClient.util'
import Title from '../container/title/Title'
import { JsonForms } from '@jsonforms/react'
import { materialRenderers, materialCells } from '@jsonforms/material-renderers'
import FeatherIcon from 'feather-icons-react'
import { createApiSchema, getApi } from '../action/Api'
import useAuth from 'hooks/useAuth'
import moment from 'moment'
import { getCheckValue } from '../action/Data'
import { convertValueBySchema } from '../element/button/Request'
import { onCallAPIAction } from '../action/condition/Action'

const JsonFormUi = (props) => {
  const designContext = useContext(DesignContext)
  const { isUiChanged, setIsUiChanged } = designContext
  const { user } = useAuth()
  const { apiData, modalAction, modal, setModal, closeModal, modalJson } = props
  const { schema, uischema, data } = modal
  const { saveAction } = modalAction
  const { tableColumn = [] } = modalJson?.uischema || {}
  const datasourceValue = objectPath.get(designContext, '@datasource') || {}
  const { formData } = apiData

  const setRequestData = async (action) => {
    let { formData, rowData } = apiData
    let requestData = {}
    let { customData = [] } = action
    const resolver = new Resolver()
    let apiSchema = await createApiSchema(action, designContext, resolver)
    let isUseNull = true
    customData.forEach((item) => {
      let {
        request,
        propType,
        value,
        valueType,
        key = '',
        typeField,
        type,
        systemKey,
      } = item
      let format = typeField || type
      if (propType === 'jsonForm') {
        if (!item.value) {
          value = data
        } else if (objectPath.has(data, item.value)) {
          value = objectPath.get(data, item.value)
        }
      } else if (propType === 'table') {
        let column = tableColumn.find((column) => column.field === key)
        format = column?.typeField || column?.type
        if (format === 'no') {
          value = objectPath.get(rowData, `tableData.${key}`)
        } else if (valueType === 'receive') {
          value = objectPath.get(rowData, key)
        } else {
          value = column?.value
        }
      } else if (propType === 'user') {
        value = objectPath.get(user, 'userId')
      } else if (propType === 'input') {
        value = objectPath.get(formData, systemKey)
      } else if (propType === 'custom') {
      } else if (propType === 'dateTime') {
        let now = null
        if (value === 'currentDate') {
          now = moment().format('YYYY-MM-DD')
        } else if (value === 'currentTime') {
          now = moment().format('HH:mm:ss')
        } else if (value === 'currentDateTime') {
          now = moment().format('YYYY-MM-DD HH:mm:ss')
        }
        value = now
      } else if (propType === 'datasource') {
        format = format ?? 'string'
        value = objectPath.get(datasourceValue, key)
      }
      value = getCheckValue(value, format, isUseNull)
      if (value !== undefined) {
        value = convertValueBySchema(apiSchema, request, value)
        objectPath.set(requestData, request, value)
      }
    })
    return requestData
  }

  const getConfig = (action) => {
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    return config
  }

  const callApi = async ({ method, api, requestData, config }) => {
    let response
    if (method === 'post') {
      response = await httpClient
        .post(api, requestData, config)
        .then((response) => doAfter(saveAction, response))
        .catch((error) => {
          if (error.response) {
            let response = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
            doAfter(saveAction, response)
          }
        })
    } else if (method === 'put') {
      response = await httpClient
        .put(api, requestData, config)
        .then((response) => doAfter(saveAction, response))
        .catch((error) => {
          if (error.response) {
            let response = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
            doAfter(saveAction, response)
          }
        })
    } else if (method === 'patch') {
      response = await httpClient
        .patch(api, requestData, config)
        .then((response) => doAfter(saveAction, response))
        .catch((error) => {
          if (error.response) {
            let response = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
            doAfter(saveAction, response)
          }
        })
    } else if (method === 'delete') {
      response = await httpClient
        .delete(api, config)
        .then((response) => doAfter(saveAction, response))
        .catch((error) => {
          if (error.response) {
            let response = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
            doAfter(saveAction, response)
          }
        })
    }
  }

  const onSave = async () => {
    if (!saveAction) return
    let { method } = saveAction
    let api = getApi(saveAction, apiData)

    let requestData = await setRequestData(saveAction)
    let config = getConfig(saveAction)
    await callApi({ method, api, requestData, config })
  }

  const doAction = (action) => {
    let { name } = action
    if (name === 'closeModal') {
      let { modalType, isRefreshTable } = action
      if (modalType === 'jsonForm') {
        closeModal()
      }
      if (isRefreshTable) {
        objectPath.set(isUiChanged, 'isRefreshTable', true)
        setIsUiChanged(isUiChanged)
      }
    }
  }

  const doAfter = (action, response) => {
    let uischema = modalJson?.uischema
    let { afterAction = {}, afterSuccess = {} } = modalJson?.action

    let { status, data = null } = response || {}
    if (status === null) return
    if (action?.dataTable) {
      let { dataKey = '', dataTable } = action
      let responseData = objectPath.get(data, dataKey)
      dataTable.forEach(({ response, dataType, key, dynamicName }) => {
        if (dataType === 'input') {
          let value = objectPath.get(responseData, response)
          objectPath.set(formData, dynamicName, value)
          objectPath.set(formData, key, value)
        }
      })
    }
    if (uischema) {
      let tableAction = uischema?.tableAction?.action
      if (tableAction) {
        let fetchAction =
          tableAction.find((item) => item.name === 'fetch') || {}
        if (fetchAction) {
          let { elementAction } = designContext
          onCallAPIAction(
            fetchAction,
            formData,
            elementAction,
            designContext,
            true
          )
        }
      }
    }
    setNotification(response)
    if (afterAction?.isEnabled) {
      let actionList = objectPath.get(afterAction, 'action') || []
      actionList.forEach(async (action) => {
        console.log('afterAction', action.name)
        doAction(action)
      })
    }
    if (afterSuccess?.isEnabled) {
      if (status >= 200 && status < 300) {
        let actionList = objectPath.get(afterSuccess, 'action') || []
        actionList.forEach(async (action) => {
          console.log('afterSuccess', action.name)
          doAction(action)
        })
      }
    }
  }

  const setNotification = ({ status, title = '', message = '' }) => {
    let { afterAction = {} } = modalJson?.action
    let {
      isNotified,
      notifyErrorText,
      notifyErrorTitle,
      notifySuccessText,
      notifySuccessTitle,
    } = afterAction
    if (!isNotified) return
    if (status >= 200 && status < 300) {
      let notice = PNotify.success({
        title: notifySuccessTitle || 'Success',
        text: notifySuccessText || message,
        icon: 'fas fa-check-circle fa-xl',
      })
      notice.on('click', function () {
        notice.close()
      })
    } else {
      let notice = PNotify.error({
        title: notifyErrorTitle || 'Error',
        text: message ? JSON.stringify(message, null, 2) : notifyErrorText,
        icon: 'fas fa-exclamation-circle fa-xl',
      })
      notice.on('click', function () {
        notice.close()
      })
    }
  }

  if (schema && uischema) {
    return (
      <Row>
        <Col sm="12">
          <JsonForms
            schema={schema}
            uischema={uischema}
            data={data}
            renderers={materialRenderers}
            cells={materialCells}
            onChange={({ errors, data }) => setModal({ ...modal, data })}
          />
          <hr />
        </Col>
        <div className="ml-auto px-2">
          <Button
            variant="outline-success"
            className="mx-1"
            onClick={() => onSave()}
          >
            <FeatherIcon icon="save" size="14" className="mr-1" />
            บันทึก
          </Button>
          <Button
            variant="outline-danger"
            className="mx-1"
            onClick={closeModal}
          >
            <FeatherIcon icon="x" size="14" className="mr-1" />
            ยกเลิก
          </Button>
        </div>
      </Row>
    )
  }
  return (
    <Row>
      <Col
        sm="12"
        style={{ fontSize: '1.5rem' }}
        className="text-center font-large text-danger my-2"
      >
        ไม่พบ {schema ? '' : 'schema'} {uischema ? '' : 'uischema'}{' '}
      </Col>
      <div className="ml-auto px-2">
        <Button variant="outline-danger" className="mx-1" onClick={closeModal}>
          <FeatherIcon icon="x" size="14" className="mr-1" />
          ยกเลิก
        </Button>
      </div>
    </Row>
  )
}

const NormalModal = (props) => {
  const { isOpenModal, closeModal, modalAction } = props
  const modalSize = modalAction?.modalSize || 'lg'
  const cardColor = modalAction?.cardColor
  const textColor = modalAction?.textColor
  const closeButton = modalAction?.closeButton

  return (
    <Modal show={isOpenModal} size={modalSize}>
      <Modal.Header
        closeButton={closeButton === undefined ? true : closeButton}
        onHide={closeModal}
        style={{
          backgroundColor: cardColor || '#fff',
        }}
      >
        <h5
          className="mb-0"
          style={{
            color: textColor || '#000',
          }}
        >
          <Title {...props} />
        </h5>
      </Modal.Header>
      <Modal.Body className="p-3">
        <JsonFormUi {...props} />
      </Modal.Body>
    </Modal>
  )
}

const FullSizeModal = (props) => {
  const { isOpenModal, closeModal, modalAction } = props
  const cardColor = modalAction?.cardColor
  const textColor = modalAction?.textColor
  const closeButton = modalAction?.closeButton

  return (
    <Modal
      fullscreen="true"
      dialogClassName={styles.modalFullScreen}
      className="pl-0"
      show={isOpenModal}
    >
      <Modal.Header
        closeButton={closeButton === undefined ? true : closeButton}
        onHide={closeModal}
        style={{
          backgroundColor: cardColor || '#fff',
        }}
      >
        <h5
          className="mb-0"
          style={{
            color: textColor || '#000',
          }}
        >
          <Title {...props} />
        </h5>
      </Modal.Header>
      <Modal.Body className="p-3">
        <JsonFormUi {...props} />
      </Modal.Body>
    </Modal>
  )
}

const ModalJsonForm = () => {
  const designContext = useContext(DesignContext)
  const { isUiChanged, setIsUiChanged, jsonForm } = designContext
  const { modalJson = {} } = isUiChanged
  const { isOpen, action = {}, rowData = {} } = modalJson
  const formData = jsonForm.formData
  const modalSize = action?.modalSize || 'lg'
  const title = action?.title

  const match = useRouteMatch()
  const location = useLocation()
  const search = location.search || []
  const pathParams = match.params
  const urlSearchParams = new URLSearchParams(search)
  const queryParams = Object.fromEntries([...urlSearchParams])
  const initialModal = {
    uischema: null,
    schema: null,
    data: null,
  }
  const apiData = {
    designContext,
    formData,
    rowData,
    queryParams,
    pathParams,
  }
  const props = {
    modalAction: action,
    uischema: { ...action, fontSize: 'unset', fontWeight: 'unset' },
    isModalTitle: true,
    isActive: false,
    modalTitle: title,
    apiData,
  }

  const [modal, setModal] = useState(initialModal)

  const closeModal = () => {
    setModal(initialModal)
    objectPath.set(isUiChanged, 'modalJson', {
      isOpen: false,
    })
    setIsUiChanged(isUiChanged)
  }

  const getJsonForm = async () => {
    let { jsonFormAction } = action
    if (!jsonFormAction) return
    let api = getApi(jsonFormAction, apiData)
    let response = await httpClient
      .get(api)
      .then((data) => data.data)
      .catch(null)
    if (response) {
      let { dataKey, jsonData, jsonSchema, jsonUischema } = jsonFormAction
      response = objectPath.get(response, dataKey)
      let schema = objectPath.get(response, jsonSchema)
      let uischema = objectPath.get(response, jsonUischema)
      let data = objectPath.get(response, jsonData)
      if (schema && uischema) {
        setModal({ uischema, schema, data })
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      getJsonForm()
    }
  }, [isOpen])

  if (modalSize === 'fullscreen') {
    return (
      <FullSizeModal
        {...props}
        isOpenModal={isOpen}
        closeModal={closeModal}
        modal={modal}
        setModal={setModal}
        modalJson={modalJson}
      />
    )
  }

  return (
    <NormalModal
      {...props}
      isOpenModal={isOpen}
      closeModal={closeModal}
      modal={modal}
      setModal={setModal}
      modalJson={modalJson}
    />
  )
}

export default ModalJsonForm
